import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Container, Typography, Button } from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { format } from "date-fns";
import { ROUTES } from "../../../constants";
import { useAuth } from "../../auth/hooks/useAuth";
import { useClientProfile } from "../../auth/hooks/useClientProfile";
import { useOrganization } from "../../auth/hooks/useOrganization";
import { useExpandedProtocol } from "../hooks/useExpandedProtocol";
import { useExercisesMap } from "../hooks/useExercisesMap";
import { useTrainingPlanTheme } from "../hooks/useTrainingPlanTheme";
import { PlanExerciseItem, PlanSuperSetItem } from "../components/PrintablePlanExercise";
import "./training-plan-view.css";

export default function PrintablePlanPage() {
  const navigate = useNavigate();
  const { clientId, organizationId, isLoading: authLoading } = useAuth();
  const { client, loading: clientLoading } = useClientProfile();
  const { organization } = useOrganization(organizationId);
  const protocolId = client?.protocolId ?? null;

  useTrainingPlanTheme(organization?.brandingColors);

  const { protocol, loading, error } = useExpandedProtocol({
    organizationId,
    protocolId,
  });
  const { getExerciseName, findById, loading: exercisesLoading } = useExercisesMap(
    organizationId,
    !!protocolId
  );

  useEffect(() => {
    document.body.classList.add("printable-plan-page");
    return () => {
      document.body.classList.remove("printable-plan-page");
    };
  }, []);

  const handlePrint = () => {
    window.print();
  };

  const waitingForProfile = authLoading || (clientId && clientLoading && !client);
  if (waitingForProfile) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 8, textAlign: "center" }}>
          <Typography color="text.secondary">Loading...</Typography>
        </Box>
      </Container>
    );
  }

  if (!protocolId) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography color="text.secondary">No plan assigned.</Typography>
          <Button className="no-print" sx={{ mt: 2 }} onClick={() => navigate(ROUTES.DASHBOARD)}>
            Back to Dashboard
          </Button>
        </Box>
      </Container>
    );
  }

  if (loading || exercisesLoading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 8, textAlign: "center" }}>
          <Typography color="text.secondary">Loading...</Typography>
        </Box>
      </Container>
    );
  }

  if (error || !protocol?.protocolData) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 4 }}>
          <Typography color="error">{error?.message ?? "Failed to load plan."}</Typography>
          <Button className="no-print" sx={{ mt: 2 }} onClick={() => navigate(ROUTES.TRAINING_PLAN)}>
            Back to Regular View
          </Button>
        </Box>
      </Container>
    );
  }

  const protocolData = protocol.protocolData;
  const splitsExercises = protocol.splitsExercises ?? [];
  const lastUpdated = protocolData.updatedOn || protocolData.createdOn;
  const lastUpdatedStr = lastUpdated ? format(new Date(lastUpdated), "MMM d, yyyy") : null;
  const orgName = organization?.name || "Training Plan";

  return (
    <Container maxWidth="md" className="printable-plan-container" disableGutters>
      <Box className="no-print printable-plan-controls">
        <Button
          startIcon={<ArrowBackIcon />}
          variant="outlined"
          onClick={() => navigate(ROUTES.TRAINING_PLAN)}
        >
          Back to Regular View
        </Button>
        <Button startIcon={<PrintIcon />} variant="contained" onClick={handlePrint}>
          Print
        </Button>
      </Box>

      <div className="printable-plan-document">
        <header className="pp-cover">
          <p className="pp-cover-org">{orgName}</p>
          <h1 className="pp-cover-title">{protocolData.name}</h1>
          {protocolData.description ? (
            <div className="pp-cover-description">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {protocolData.description}
              </ReactMarkdown>
            </div>
          ) : null}
          <div className="pp-cover-meta">
            <span>
              {splitsExercises.length} workout{splitsExercises.length !== 1 ? "s" : ""}
            </span>
            {lastUpdatedStr ? <span>Last updated {lastUpdatedStr}</span> : null}
          </div>
        </header>

        <h2 className="pp-section-heading">Training Schedule</h2>

        {splitsExercises.map((workout, index) => {
          const isRest = workout?.isRestSplit ?? false;
          const exercises = workout?.exercises ?? [];
          const headerAlt = index % 2 === 1 ? " pp-workout-header--alt" : "";

          if (isRest) {
            return (
              <section key={workout.id ?? index} className="pp-workout pp-workout--rest">
                <div className="pp-workout-header">
                  <span className="pp-workout-day">Day {index + 1}</span>
                  <h3 className="pp-workout-name">{workout?.name || "Rest Day"}</h3>
                </div>
                {workout?.description ? (
                  <div className="pp-workout-body">
                    <p className="pp-workout-description">{workout.description}</p>
                  </div>
                ) : null}
              </section>
            );
          }

          return (
            <section key={workout.id ?? index} className="pp-workout">
              <div className={`pp-workout-header${headerAlt}`}>
                <span className="pp-workout-day">Day {index + 1}</span>
                <h3 className="pp-workout-name">{workout?.name || `Workout ${index + 1}`}</h3>
              </div>
              <div className="pp-workout-body">
                {workout?.description ? (
                  <p className="pp-workout-description">{workout.description}</p>
                ) : null}
                <div className="pp-exercise-list">
                  {exercises.map((ex, exIdx) =>
                    ex?.type === "super_set" ? (
                      <PlanSuperSetItem
                        key={ex.id ?? exIdx}
                        exercise={ex}
                        index={exIdx}
                        findById={findById}
                        getExerciseName={getExerciseName}
                      />
                    ) : (
                      <PlanExerciseItem
                        key={ex.id ?? exIdx}
                        exercise={ex}
                        index={exIdx}
                        findById={findById}
                        getExerciseName={getExerciseName}
                      />
                    )
                  )}
                </div>
              </div>
            </section>
          );
        })}
      </div>
    </Container>
  );
}
